import { clsx } from 'clsx';
import type { ReactNode } from 'react';

/**
 * The pattern all three signed-in dashboards are built from: a thing to do, why, and the button
 * that does it.
 *
 * The `why` line is required. A queue of bare titles — "Publish seating", "Send recap" — is a list
 * of chores; a queue that says *why now* is a colleague telling you what happened. It is the
 * difference between the dashboard being read and being scrolled past.
 *
 * The left bar is the only urgency signal, and there are two states rather than a scale: `--signal`
 * means act now, `--light` means when you can. A five-level priority system invites everything to
 * be level five.
 */
export function ActionQueueItem({
  title,
  why,
  urgency = 'when-you-can',
  action,
  className,
}: {
  title: string;
  /** One line of plain English. Not a restatement of the title. */
  why: ReactNode;
  urgency?: 'act-now' | 'when-you-can';
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={clsx(
        'flex items-center justify-between gap-4 border-b border-light py-3.5 pl-4 last:border-b-0',
        /*
         * Colour alone must not carry the meaning — the urgent ones also sort to the top, and the
         * `why` line says what is happening. This is the glance-level cue, not the only one.
         */
        'border-l-[3px]',
        urgency === 'act-now' ? 'border-l-signal' : 'border-l-light',
        className,
      )}
    >
      <div className="min-w-0">
        <p className="text-sm font-semibold">{title}</p>
        <p className="mt-0.5 text-[13px] leading-snug text-ink-2">{why}</p>
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}
